
import { defineComponent, PropType, computed, inject, ComputedRef, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'primevue/button';
import FieldErrors from '@/components/fields/partials/FieldErrors.vue';
import { getOperatorById, OperatorInsertContext } from '@/components/flows/operators/registry';
import { parseOperatorParameters } from '@/helpers/flowParameters';
import { startLinkedOperatorSelection, consumeLastConfirmedSelection } from '@/composables/flows/useLinkedOperatorSelection';
import type { FlowStructureDto } from '@/types/flow-structure';

export interface LinkableOperator {
  stepId: string;
  type: string;
  icon: string;
  label: string;
  customTitle?: string;
}

export default defineComponent({
  name: 'LinkedOperatorSelector',

  components: {
    Button,
    FieldErrors,
  },

  props: {
    modelValue: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    
    // List of operator types that can be linked (e.g., ['boxsendemail', 'boxsendfbmessenger'])
    linkableTypes: {
      type: Array as PropType<string[]>,
      required: true,
    },
    
    // Current operator's step ID (to find ancestors) - used in edit mode
    operatorId: {
      type: String,
      default: '',
    },
    
    formValidation: {
      type: Object,
      default: null,
    },
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const { t } = useI18n();
    
    // Inject flow and insert context from parent
    const flow = inject<ComputedRef<FlowStructureDto | null>>('flow', computed(() => null));
    const insertContext = inject<ComputedRef<OperatorInsertContext | null>>('insertContext', computed(() => null));

    // Get operator info from step ID
    const getOperatorInfo = (stepId: string): LinkableOperator | null => {
      if (!flow.value) return null;
      
      const step = flow.value.steps.find(s => s.id === stepId);
      if (!step) return null;
      
      const metadata = getOperatorById(step.type);
      const params = parseOperatorParameters(step.parameters);
      
      return {
        stepId: step.id,
        type: step.type,
        icon: metadata?.icon ?? 'fa-box',
        label: metadata ? t(metadata.label) : step.type,
        customTitle: params.custom_title as string || undefined,
      };
    };

    // Get selected operators with their info
    const selectedOperators = computed<LinkableOperator[]>(() => {
      if (!props.modelValue.length) return [];
      
      // If flow is not available, show basic info with step IDs
      if (!flow.value) {
        return props.modelValue.map(stepId => ({
          stepId,
          type: 'unknown',
          icon: 'fa-box',
          label: stepId,
          customTitle: undefined,
        }));
      }
      
      return props.modelValue
        .map(stepId => getOperatorInfo(stepId))
        .filter((op): op is LinkableOperator => op !== null);
    });

    // Remove a selection
    const removeSelection = (stepId: string) => {
      const newValue = props.modelValue.filter(id => id !== stepId);
      emit('update:modelValue', newValue);
    };

    // Inject callbacks from parent
    const reopenPanel = inject<(() => void) | null>('reopenPanel', null);
    const handleStartSelection = inject<(() => void) | null>('handleStartSelection', null);

    // Get all ancestor step IDs for a given step (for restricting selection to ancestors only)
    const getAncestorStepIds = (stepId: string): string[] => {
      if (!flow.value) return [];
      const ancestorIds: string[] = [];
      const visited = new Set<string>();

      const collectAncestors = (currentId: string) => {
        if (visited.has(currentId)) return;
        visited.add(currentId);

        const step = flow.value!.steps.find(s => s.id === currentId);
        if (!step) return;

        // Add primary parent
        if (step.primaryParent?.id) {
          ancestorIds.push(step.primaryParent.id);
          collectAncestors(step.primaryParent.id);
        }

        // Add secondary parents
        step.parents?.forEach(parent => {
          if (parent.id && !ancestorIds.includes(parent.id)) {
            ancestorIds.push(parent.id);
            collectAncestors(parent.id);
          }
        });
      };

      collectAncestors(stepId);
      return ancestorIds;
    };

    // Get selectable step IDs (ancestors of the current operator being configured)
    const getSelectableStepIds = (): string[] => {
      // If we have an operatorId (edit mode), use its ancestors
      if (props.operatorId) {
        return getAncestorStepIds(props.operatorId);
      }
      // If we have insert context with allAncestorStepIds, use it directly
      const context = insertContext.value;
      if (context?.allAncestorStepIds && context.allAncestorStepIds.length > 0) {
        return context.allAncestorStepIds;
      }
      return [];
    };

    // Check for pending confirmed selection when component mounts (after panel reopens)
    onMounted(() => {
      const confirmedSelection = consumeLastConfirmedSelection();
      if (confirmedSelection !== null) {
        emit('update:modelValue', confirmedSelection);
      }
    });

    // Open flow selection mode
    const openFlowSelection = () => {
      // Get the list of ancestor step IDs that can be selected
      const selectableStepIds = getSelectableStepIds();
      
      // Start the selection mode with callbacks
      startLinkedOperatorSelection(
        props.linkableTypes,
        selectableStepIds,
        [...props.modelValue],
        (selectedStepIds: string[]) => {
          // On confirm: update the model value
          emit('update:modelValue', selectedStepIds);
        },
        () => {
          // On cancel: do nothing, keep current selection
        },
        reopenPanel || undefined
      );
      
      // Hide the panel when entering selection mode
      if (handleStartSelection) {
        handleStartSelection();
      }
    };

    return {
      t,
      selectedOperators,
      removeSelection,
      openFlowSelection,
    };
  },
});
